import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export function usePick(semanaId) {
  const { membroAtual, ligaAtual } = useAuth()
  const membroId = membroAtual?.id
  const [pick, setPick] = useState(null)
  const [loading, setLoading] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState(null)
  const [sucesso, setSucesso] = useState(false)
  const [timesUsados, setTimesUsados] = useState([])

  const carregarPick = useCallback(async () => {
    if (!semanaId || !membroId) return
    setLoading(true)
    const { data } = await supabase
      .from('picks')
      .select('*')
      .eq('semana_id', semanaId)
      .eq('membro_id', membroId)
      .maybeSingle()
    setPick(data || null)
    setLoading(false)
  }, [semanaId, membroId])

  useEffect(() => { carregarPick() }, [carregarPick])

  useEffect(() => {
    async function carregarTimesUsados() {
      if (!membroId || !ligaAtual?.id) return
      try {
        const { data, error } = await supabase
          .from('times_usados')
          .select('time_abbr, semana_numero')
          .eq('membro_id', membroId)
          .eq('liga_id', ligaAtual.id)
          .order('semana_numero', { ascending: true })
          .limit(32)
        if (!error && data) setTimesUsados(data.map(t => t.time_abbr))
        else setTimesUsados([])
      } catch { setTimesUsados([]) }
    }
    carregarTimesUsados()
  }, [membroId, ligaAtual?.id, pick])

  const confirmarPick = useCallback(async (timeEscolhido, jogoId, spreadAtual) => {
    if (!semanaId || !membroId) return { erro: 'Sem autenticação' }
    setSalvando(true); setErro(null); setSucesso(false)

    try {
      const { data: semana } = await supabase
        .from('semanas').select('picks_abertos, deadline, tipo, numero').eq('id', semanaId).maybeSingle()

      if (!semana?.picks_abertos) { setErro('O prazo para picks já encerrou.'); setSalvando(false); return { erro: 'Deadline encerrado' } }
      if (semana.deadline && new Date() > new Date(semana.deadline)) { setErro('O deadline já passou.'); setSalvando(false); return { erro: 'Deadline passou' } }

      const ehPlayoffs = semana.tipo !== 'regular'

      // Verifica se time já foi usado (apenas temporada regular)
      if (!ehPlayoffs) {
        const { data: jaUsado } = await supabase
          .from('times_usados').select('time_abbr')
          .eq('membro_id', membroId).eq('liga_id', ligaAtual.id).eq('time_abbr', timeEscolhido)
          .maybeSingle()
        if (jaUsado) { setErro(`Você já usou ${timeEscolhido} nesta temporada.`); setSalvando(false); return { erro: 'Time já usado' } }
      }

      // Busca pick anterior ANTES do upsert
      const { data: pickAnterior } = await supabase
        .from('picks').select('time_escolhido').eq('membro_id', membroId).eq('semana_id', semanaId).maybeSingle()

      // Upsert do pick
      const { data: pickSalvo, error } = await supabase
        .from('picks').upsert({
          membro_id: membroId, semana_id: semanaId, jogo_id: jogoId,
          time_escolhido: timeEscolhido, spread_congelado: spreadAtual,
          confirmado_em: new Date().toISOString(), resultado: null,
        }, { onConflict: 'membro_id,semana_id' }).select().single()

      if (error) throw error

      // Atualiza times usados
      if (!ehPlayoffs) {
        if (pickAnterior?.time_escolhido && pickAnterior.time_escolhido !== timeEscolhido) {
          await supabase.from('times_usados').delete()
            .eq('membro_id', membroId).eq('liga_id', ligaAtual.id).eq('time_abbr', pickAnterior.time_escolhido)
        }
        await supabase.from('times_usados').upsert({
          membro_id: membroId, liga_id: ligaAtual.id,
          time_abbr: timeEscolhido, semana_numero: semana.numero,
        }, { onConflict: 'membro_id,liga_id,time_abbr' })
      }

      setPick(pickSalvo)
      setSucesso(true)
      setTimeout(() => setSucesso(false), 4000)
      return { sucesso: true, pick: pickSalvo }
    } catch (err) {
      setErro('Erro ao salvar pick. Tente novamente.')
      return { erro: err.message }
    } finally { setSalvando(false) }
  }, [semanaId, membroId, ligaAtual?.id])

  return { pick, loading, salvando, erro, sucesso, timesUsados, confirmarPick, recarregar: carregarPick }
}
